import { useMemo, useState } from 'react';
import { ResponsiveLine, type LineSeries } from '@nivo/line';

import { lineTheme } from '@/components/Graphs/Charts/Line/LineHelpers';
import type { HistoricalScoreYearPoint } from '@/services/evidenceTimelineService';

type HistoricalScoreLineChartProps = {
  points: HistoricalScoreYearPoint[];
  loading?: boolean;
  rangeLabel?: string;
  secondaryMetric?: HistoricalScoreSecondaryMetric;
  onSecondaryMetricChange?: (metric: HistoricalScoreSecondaryMetric) => void;
};

export type HistoricalScoreSecondaryMetric = 'publications' | 'clinicalTrials';

type ChartDatum = {
  x: string;
  y: number;
  rawScore?: number;
  rawSecondary?: number;
};

const rootClass = 'flex h-full w-full flex-col';
const rootStyle = { minHeight: 'min(36vh, 300px)', minWidth: 240 } as const;
const plotClass = 'flex min-h-0 flex-1 flex-col w-full rounded-lg border border-slate-100 bg-white';
const labelStyle = { fontSize: '11px' } as const;

const SCORE_COLOR = '#be0080';
const SECONDARY_COLOR = '#2563eb';

const SECONDARY_METRIC_CONFIG: Record<
  HistoricalScoreSecondaryMetric,
  { seriesId: string; axisLabel: string; legendLabel: string; tooltipLabel: string }
> = {
  publications: {
    seriesId: 'Publications',
    axisLabel: 'Publications',
    legendLabel: 'Annual publications',
    tooltipLabel: 'Publications',
  },
  clinicalTrials: {
    seriesId: 'Clinical trials',
    axisLabel: 'Clinical trials',
    legendLabel: 'Annual clinical trials',
    tooltipLabel: 'Clinical trials',
  },
};

function buildYScale(values: number[]) {
  if (values.length === 0) return { min: 0, max: 1 };
  const min = Math.min(...values);
  const max = Math.max(...values);
  const padding = Math.max((max - min) * 0.1, 0.02);
  return {
    min: Math.max(0, min - padding),
    max: max + padding,
  };
}

function mapSecondaryToScoreScale(
  value: number,
  maxSecondary: number,
  scoreScale: { min: number; max: number },
): number {
  if (maxSecondary <= 0) return scoreScale.min;
  const span = scoreScale.max - scoreScale.min;
  return scoreScale.min + (value / maxSecondary) * span;
}

function getSecondaryValue(
  point: HistoricalScoreYearPoint,
  metric: HistoricalScoreSecondaryMetric,
): number | null {
  const value = metric === 'publications' ? point.publications : point.clinicalTrials;
  return value != null && Number.isFinite(value) ? value : null;
}

export function HistoricalScoreLineChart({
  points,
  loading = false,
  rangeLabel,
  secondaryMetric: secondaryMetricProp,
  onSecondaryMetricChange,
}: HistoricalScoreLineChartProps) {
  const [internalSecondaryMetric, setInternalSecondaryMetric] =
    useState<HistoricalScoreSecondaryMetric>('publications');
  const secondaryMetric = secondaryMetricProp ?? internalSecondaryMetric;

  const setSecondaryMetric = (metric: HistoricalScoreSecondaryMetric) => {
    if (secondaryMetricProp == null) {
      setInternalSecondaryMetric(metric);
    }
    onSecondaryMetricChange?.(metric);
  };
  const secondaryConfig = SECONDARY_METRIC_CONFIG[secondaryMetric];

  const scoreValues = useMemo(
    () =>
      points.map(p => p.score).filter((value): value is number => value != null && Number.isFinite(value)),
    [points],
  );

  const secondaryValues = useMemo(
    () =>
      points
        .map(p => getSecondaryValue(p, secondaryMetric))
        .filter((value): value is number => value != null),
    [points, secondaryMetric],
  );

  const scoreScale = useMemo(() => buildYScale(scoreValues), [scoreValues]);
  const secondaryScale = useMemo(() => buildYScale(secondaryValues), [secondaryValues]);
  const plotScale = scoreValues.length > 0 ? scoreScale : secondaryScale;
  const maxSecondary = useMemo(
    () => (secondaryValues.length > 0 ? Math.max(...secondaryValues) : 0),
    [secondaryValues],
  );

  const chartData = useMemo<LineSeries[]>(() => {
    const series: LineSeries[] = [];

    if (scoreValues.length > 0) {
      series.push({
        id: 'Score',
        data: points
          .filter(p => p.score != null && Number.isFinite(p.score))
          .map(
            (p): ChartDatum => ({
              x: String(p.year),
              y: p.score as number,
              rawScore: p.score as number,
            }),
          ),
      });
    }

    if (secondaryValues.length > 0) {
      series.push({
        id: secondaryConfig.seriesId,
        data: points
          .map((p): ChartDatum | null => {
            const secondary = getSecondaryValue(p, secondaryMetric);
            if (secondary == null) return null;
            const y =
              scoreValues.length > 0
                ? mapSecondaryToScoreScale(secondary, maxSecondary, scoreScale)
                : secondary;
            return {
              x: String(p.year),
              y,
              rawSecondary: secondary,
            };
          })
          .filter((datum): datum is ChartDatum => datum != null),
      });
    }

    return series;
  }, [
    maxSecondary,
    points,
    scoreScale,
    scoreValues.length,
    secondaryConfig.seriesId,
    secondaryMetric,
    secondaryValues.length,
  ]);

  const hasChartData = chartData.some(series => series.data.length > 0);
  const showSecondaryAxis = secondaryValues.length > 0 && scoreValues.length > 0;
  const scoreSpan = scoreScale.max - scoreScale.min;

  const metricToggle = (
    <div className="mb-2 flex shrink-0 flex-wrap gap-1">
      {(Object.keys(SECONDARY_METRIC_CONFIG) as HistoricalScoreSecondaryMetric[]).map(metric => {
        const isActive = secondaryMetric === metric;
        return (
          <button
            key={metric}
            type="button"
            onClick={() => setSecondaryMetric(metric)}
            className={`rounded-full border px-2.5 py-1 text-xs font-medium transition ${
              isActive
                ? 'border-blue bg-blue text-white'
                : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
            }`}
          >
            {SECONDARY_METRIC_CONFIG[metric].axisLabel}
          </button>
        );
      })}
    </div>
  );

  const header = (
    <div className="mb-2 flex shrink-0 items-baseline justify-between gap-2">
      <p className="font-semibold uppercase tracking-wide text-slate-500" style={labelStyle}>
        Score & {secondaryConfig.axisLabel.toLowerCase()} by year
      </p>
      {rangeLabel ? (
        <p className="text-slate-400" style={labelStyle}>
          {rangeLabel}
        </p>
      ) : null}
    </div>
  );

  const legend = hasChartData ? (
    <div className="mb-2 flex shrink-0 flex-wrap gap-3">
      {scoreValues.length > 0 ? (
        <div className="flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: SCORE_COLOR }} />
          <span className="text-xs text-slate-600">Annual score</span>
        </div>
      ) : null}
      {secondaryValues.length > 0 ? (
        <div className="flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: SECONDARY_COLOR }} />
          <span className="text-xs text-slate-600">{secondaryConfig.legendLabel}</span>
        </div>
      ) : null}
    </div>
  ) : null;

  if (loading) {
    return (
      <div className={rootClass} style={rootStyle}>
        {header}
        {metricToggle}
        <div
          className={`${plotClass} items-center justify-center border-dashed border-slate-200 bg-slate-50/60`}
        >
          <p className="text-sm text-gray-500">Loading chart…</p>
        </div>
      </div>
    );
  }

  if (!hasChartData) {
    return (
      <div className={rootClass} style={rootStyle}>
        {header}
        {metricToggle}
        <div
          className={`${plotClass} items-center justify-center border-dashed border-slate-200 bg-slate-50/60 px-3 text-center`}
        >
          <div className="flex flex-col items-center gap-1">
            <p className="text-sm font-medium text-gray-500">No score data for this range.</p>
            {rangeLabel ? <p className="text-xs text-gray-400">Range: {rangeLabel}</p> : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={rootClass} style={rootStyle}>
      {header}
      {metricToggle}
      {legend}
      <div className={plotClass}>
        <ResponsiveLine
          data={chartData}
          margin={{
            top: 12,
            right: showSecondaryAxis ? 56 : 24,
            bottom: 56,
            left: 56,
          }}
          theme={lineTheme}
          colors={series => (series.id === 'Score' ? SCORE_COLOR : SECONDARY_COLOR)}
          lineWidth={2}
          curve="monotoneX"
          enablePoints
          pointSize={8}
          pointBorderWidth={2}
          pointBorderColor="#ffffff"
          useMesh
          xScale={{ type: 'point' }}
          yScale={{
            type: 'linear',
            min: plotScale.min,
            max: plotScale.max,
            stacked: false,
          }}
          axisBottom={{
            tickSize: 4,
            tickPadding: 8,
            tickRotation: (chartData[0]?.data.length ?? 0) > 8 ? -65 : 0,
            legend: 'Year',
            legendOffset: 44,
            legendPosition: 'middle',
          }}
          axisLeft={{
            tickSize: 4,
            tickPadding: 8,
            legend: scoreValues.length > 0 ? 'Score' : secondaryConfig.axisLabel,
            legendOffset: -48,
            legendPosition: 'middle',
            format: (value: number) =>
              scoreValues.length > 0 ? value.toFixed(2) : String(Math.round(value)),
          }}
          axisRight={
            showSecondaryAxis
              ? {
                  tickSize: 4,
                  tickPadding: 8,
                  legend: secondaryConfig.axisLabel,
                  legendOffset: 48,
                  legendPosition: 'middle',
                  format: (value: number) => {
                    if (scoreSpan <= 0 || maxSecondary <= 0) return '0';
                    const scaled = ((value - scoreScale.min) / scoreSpan) * maxSecondary;
                    return String(Math.round(scaled));
                  },
                }
              : null
          }
          tooltip={({ point }) => {
            const datum = point.data as ChartDatum;

            return (
              <div className="rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs shadow-md">
                <div className="font-semibold text-slate-800">{datum.x}</div>
                {datum.rawScore != null ? (
                  <div className="text-slate-600">Score: {datum.rawScore.toFixed(3)}</div>
                ) : null}
                {datum.rawSecondary != null ? (
                  <div className="text-slate-600">
                    {secondaryConfig.tooltipLabel}: {datum.rawSecondary}
                  </div>
                ) : null}
              </div>
            );
          }}
        />
      </div>
    </div>
  );
}
